/**
 * OpenReplay session replay.
 *
 * Like GA, the tracker only starts in production builds so local development
 * sessions are not recorded. Sessions are linked to the internal user_id (never
 * email/name), and every text input is masked by default; mark extra sensitive
 * regions with the `data-openreplay-obscured` attribute.
 */
// The tracker is loaded on demand (separate chunk) so it never delays the
// first screen; calls made before it arrives run in order once it has loaded.
let trackerPromise = null;
const withTracker = (fn) => {
  if (!trackerPromise) trackerPromise = import('@openreplay/tracker').then((m) => m.tracker);
  return trackerPromise.then(fn).catch((err) => console.warn('[OpenReplay]', err));
};

const PROJECT_KEY = process.env.REACT_APP_OPENREPLAY_PROJECT_KEY || 'Ftah5De5E5qL5tvgSjta';
const INGEST_POINT = process.env.REACT_APP_OPENREPLAY_INGEST_POINT || 'https://replay.edulab.in/ingest';

// Set REACT_APP_OPENREPLAY_ENABLED=true to record outside production builds
// (e.g. to verify the integration with `npm start`).
const isEnabled = () =>
  !!PROJECT_KEY &&
  (process.env.NODE_ENV === 'production' || process.env.REACT_APP_OPENREPLAY_ENABLED === 'true');

let started = false;
let currentUserId = null;

const logStart = (result) => {
  if (result && result.success === false) {
    console.warn('[OpenReplay] session not started:', result.reason);
  }
};

export function initOpenReplay() {
  if (!isEnabled() || started) return;
  started = true;

  withTracker((tracker) => {
    tracker.configure({
      projectKey: PROJECT_KEY,
      ingestPoint: INGEST_POINT,
      defaultInputMode: 1, // 0 = plain, 1 = obscured, 2 = ignored
      obscureInputEmails: true,
      obscureInputNumbers: true,
      obscureTextEmails: true,
      // The tracker refuses to run on plain-http origins (e.g. http://localhost) unless this is set.
      __DISABLE_SECURE_MODE: window.location.protocol !== 'https:',
    });
    tracker.start().then(logStart).catch((err) => console.warn('[OpenReplay] start failed:', err));
  });
}

/**
 * Link the current recording to the logged-in user. Called with null on logout.
 * When the identity changes (logout, impersonation, another user logging in on
 * the same browser) a fresh session is started so one recording never mixes
 * two people.
 */
export function identifyOpenReplayUser(user, { impersonating = false } = {}) {
  if (!started) return;
  withTracker((tracker) => applyIdentity(tracker, user, impersonating));
}

function applyIdentity(tracker, user, impersonating) {
  const userId = user?.user_id != null ? String(user.user_id) : null;
  const metadata = {
    role: user?.role || '',
    impersonating: impersonating ? 'yes' : 'no',
  };

  if (userId === currentUserId) {
    if (userId) Object.entries(metadata).forEach(([k, v]) => tracker.setMetadata(k, v));
    return;
  }

  const hadUser = currentUserId !== null;
  currentUserId = userId;

  if (hadUser) {
    tracker.stop();
    tracker
      .start(userId ? { forceNew: true, userID: userId, metadata } : { forceNew: true })
      .then(logStart)
      .catch((err) => console.warn('[OpenReplay] restart failed:', err));
  } else if (userId) {
    tracker.setUserID(userId);
    Object.entries(metadata).forEach(([k, v]) => tracker.setMetadata(k, v));
  }
}
